import ErrorComponent from "@/components/atoms/ErrorComponent";
import Loader from "@/components/atoms/Loader";
import SoftBadge from "@/components/atoms/SoftBadge";
import AreYouSurToContinueModal from "@/components/molecules/modals/AreYouSurToContinueModal";
import AllTeamsHeader from "@/components/molecules/table-headers/AllTeamsHeader";
import Table from "@/components/molecules/table/Table";
import { splitText } from "@/helpers/utils";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useTableState } from "@/hooks/useTableState";
import { Team, TeamFilters } from "@/interfaces/Team";
import { useDestroyTeamMutation, useGetTeamsQuery } from "@/services/teams";
import { faEdit, faEye, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { ColumnDef, TableState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { ElementRef, FC, Fragment, useRef } from "react";
import { Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ToastContentProps, toast } from "react-toastify";

const TeamsView: FC = () => {
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const [deleteTeamAsync] = useDestroyTeamMutation();

  const [state, setState] = useTableState<Partial<TableState> & TeamFilters>(
    "teams",
    {
      pagination: {
        pageIndex: 0,
        pageSize: 15,
      },
      globalFilter: "",
    },
  );

  const debouncedQuery = useDebouncedValue(state.globalFilter, 500);

  const { data, isLoading, isError, error } = useGetTeamsQuery({
    ...state.pagination,
    query: debouncedQuery,
    ...(state.sorting?.[0] && {
      sortBy: state.sorting[0].id,
      sortDirection: state.sorting[0].desc ? "desc" : "asc",
    }),
  });

  const onDelete = async (id: string) => {
    toast.promise(deleteTeamAsync(id).unwrap(), {
      pending: "Suppression de l'équipe en cours ...",
      success: {
        render() {
          DeleteModalRef.current?.showOrHiddenModal();
          return (
            <p className="mb-0 text-center">L'équipe a bien été supprimée</p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          return "Une erreur est survenue";
        },
      },
    });
  };

  const columns = useRef<ColumnDef<Team>[]>([
    {
      header: "Actions",
      enableSorting: false,
      footer: (props) => props.column.id,
      accessorFn: ({ id }) => (
        <Fragment>
          <Link to={`/teams/${id}`} className="btn btn-primary btn-sm">
            <FontAwesomeIcon icon={faEye} />
          </Link>
          <Link to={`/teams/${id}/edit`} className="btn btn-info btn-sm ms-2">
            <FontAwesomeIcon icon={faEdit} />
          </Link>
          <Button
            variant="danger"
            size="sm"
            className="ms-2"
            onClick={() => DeleteModalRef.current?.showOrHiddenModal(id)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </Fragment>
      ),
      id: "actions",
      cell: (info) => info.getValue(),
    },
    {
      header: "#",
      footer: (props) => props.column.id,
      accessorFn: ({ id }) => splitText(id, 10),
      id: "id",
      cell: (info) => info.getValue(),
    },
    {
      header: "Nom",
      footer: (props) => props.column.id,
      accessorFn: ({ name, color }) => (
        <SoftBadge bg={color || "secondary"} textColor={color ? "#fff" : ""}>
          {name}
        </SoftBadge>
      ),
      id: "name",
      cell: (info) => info.getValue(),
    },
    {
      header: "Description",
      footer: (props) => props.column.id,
      accessorFn: ({ description }) => description || "Aucune",
      id: "description",
      cell: (info) => info.getValue(),
    },
    {
      header: "Crée le",
      footer: (props) => props.column.id,
      accessorFn: (row) =>
        DateTime.fromISO(row.createdAt).toFormat("dd-MM-yyyy"),
      id: "createdAt",
      cell: (info) => info.getValue(),
    },
  ]).current;

  if (isLoading) {
    return <Loader absolute />;
  } else if (isError) {
    return <ErrorComponent error={error} absolute returnButton />;
  }

  return (
    <div className="formalys-page formalys-list-page">
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDelete}
        title="Souhaitez-vous vraiment supprimé l'équipe qui a pour id : {{id}} ?"
      />
      <Table
        resource="teams"
        tableName="teams"
        HeaderComponent={AllTeamsHeader}
        columns={columns}
        state={state}
        onStateChange={setState}
        data={data?.data}
        meta={data?.meta}
      />
    </div>
  );
};

export default TeamsView;
